import api from '../../../core/api/client';

const BASE = '/events';
const byId = (id) => `${BASE}/${encodeURIComponent(id)}`;
const sub  = (id, path) => `${byId(id)}/${path}`;

const toApiError = (error) => {
  if (error?.response) {
    const d = error.response.data;
    return {
      status:  error.response.status,
      message: d?.detail || d?.message || d?.error || error.message || 'Request failed',
      data:    d,
    };
  }
  return { status: 0, message: error?.message || 'Unexpected error', data: null };
};

const wrap = async (fn) => {
  try { return await fn(); }
  catch (error) { throw toApiError(error); }
};

export const eventsService = {
  async list({ eventType, mode, status, organizerId, tag, from, to } = {}) {
    return wrap(async () => {
      const params = {};
      if (eventType)   params.event_type   = eventType;
      if (mode)        params.mode         = mode;
      if (status)      params.status       = status;
      if (organizerId) params.organizer_id = organizerId;
      if (tag)         params.tag          = tag;
      if (from)        params.from         = from;
      if (to)          params.to           = to;
      const res = await api.get(BASE, { params });
      return res.data;
    });
  },

  async create(data) {
    return wrap(async () => {
      const res = await api.post(BASE, data);
      return res.data;
    });
  },

  async getUpcoming({ limit } = {}) {
    return wrap(async () => {
      const params = {};
      if (limit) params.limit = limit;
      const res = await api.get(`${BASE}/upcoming`, { params });
      return res.data;
    });
  },

  async get(eventId) {
    return wrap(async () => {
      const res = await api.get(byId(eventId));
      return res.data;
    });
  },

  async update(eventId, patch) {
    return wrap(async () => {
      const res = await api.patch(byId(eventId), patch);
      return res.data;
    });
  },

  async delete(eventId) {
    return wrap(async () => {
      const res = await api.delete(byId(eventId));
      return res.data;
    });
  },

  async changeStatus(eventId, status) {
    return wrap(async () => {
      const res = await api.patch(sub(eventId, 'status'), { status });
      return res.data;
    });
  },

  async saveRecap(eventId, recap) {
    return wrap(async () => {
      const res = await api.patch(sub(eventId, 'recap'), { recap });
      return res.data;
    });
  },

  async addChecklistItem(eventId, { item, assigneeId, dueAt } = {}) {
    return wrap(async () => {
      const body = { item };
      if (assigneeId) body.assignee_id = assigneeId;
      if (dueAt)      body.due_at      = dueAt;
      const res = await api.post(sub(eventId, 'checklist'), body);
      return res.data;
    });
  },

  async updateChecklistItem(eventId, itemId, patch) {
    return wrap(async () => {
      const res = await api.patch(`${sub(eventId, 'checklist')}/${encodeURIComponent(itemId)}`, patch);
      return res.data;
    });
  },

  async deleteChecklistItem(eventId, itemId) {
    return wrap(async () => {
      const res = await api.delete(`${sub(eventId, 'checklist')}/${encodeURIComponent(itemId)}`);
      return res.data;
    });
  },

  async addNote(eventId, { body: noteBody }) {
    return wrap(async () => {
      const res = await api.post(sub(eventId, 'notes'), { body: noteBody });
      return res.data;
    });
  },

  async deleteNote(eventId, noteId) {
    return wrap(async () => {
      const res = await api.delete(`${sub(eventId, 'notes')}/${encodeURIComponent(noteId)}`);
      return res.data;
    });
  },
};

export const EVENT_STATUSES = ['planning', 'scheduled', 'ongoing', 'completed', 'cancelled'];
export const EVENT_TYPES    = ['outreach', 'recruitment', 'training', 'ideation', 'engagement', 'speaker_talk', 'hackathon', 'conference'];
export const EVENT_MODES    = ['in_person', 'virtual', 'hybrid'];
